import React, { useState } from 'react';
import { usersAPI, analyticsAPI } from '../../services/api';
import Toast from '../../components/common/Toast';

const ReportsPage = () => {
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [userId, setUserId] = useState('');
  const [dateRange, setDateRange] = useState({
    start: '',
    end: new Date().toISOString().split('T')[0]
  });

  const handleExportUserData = async () => {
    if (!userId) {
      setToast({ show: true, message: 'Please enter a User ID', type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const response = await usersAPI.exportUserData(userId);
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `user-${userId}-export-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setToast({ show: true, message: 'User data exported successfully', type: 'success' });
    } catch (error) {
      console.error('Export error:', error);
      setToast({ show: true, message: error.response?.data?.message || 'Failed to export user data', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleExportAnalytics = async () => {
    if (!dateRange.start || !dateRange.end) {
      setToast({ show: true, message: 'Please select both start and end dates', type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const response = await analyticsAPI.exportAnalytics(dateRange.start, dateRange.end);
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nisa-care-analytics-${dateRange.start}-to-${dateRange.end}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setToast({ show: true, message: 'Analytics exported successfully', type: 'success' });
    } catch (error) {
      console.error('Export error:', error);
      setToast({ show: true, message: error.response?.data?.message || 'Failed to export analytics', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />

      <div className="page-header mb-4">
        <h2 className="page-title">Reports & Data Export</h2>
        <p className="page-subtitle">Export user data for GDPR requests, legal compliance, or analytics</p>
      </div>

      <div className="row g-3">
        <div className="col-lg-6">
          <div className="stat-card">
            <h5 className="mb-3"><i className="bi bi-person-lines-fill me-2"></i>User Data Export (GDPR)</h5>
            <p className="text-muted small">Export complete user data including profile, habits, prayers, qada, notes, and analytics.</p>

            <div className="mb-3">
              <label className="form-label">User ID *</label>
              <input
                type="number"
                className="form-control"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="Enter User ID"
              />
            </div>

            <button className="btn btn-primary" onClick={handleExportUserData} disabled={loading}>
              <i className="bi bi-download me-2"></i>
              {loading ? 'Exporting...' : 'Export User Data (CSV)'}
            </button>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="stat-card">
            <h5 className="mb-3"><i className="bi bi-graph-up me-2"></i>Analytics Report</h5>
            <p className="text-muted small">Export user analytics, engagement metrics, and content performance.</p>

            <div className="mb-3">
              <label className="form-label">Date Range</label>
              <div className="row g-2">
                <div className="col-6">
                  <input
                    type="date"
                    className="form-control"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  />
                </div>
                <div className="col-6">
                  <input
                    type="date"
                    className="form-control"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <button className="btn btn-primary" onClick={handleExportAnalytics} disabled={loading}>
              <i className="bi bi-download me-2"></i>
              {loading ? 'Exporting...' : 'Export Analytics (CSV)'}
            </button>
          </div>
        </div>
      </div>

      <div className="stat-card mt-3">
        <h5 className="mb-3"><i className="bi bi-shield-check me-2"></i>Compliance Notes</h5>
        <ul className="mb-0">
          <li><strong>GDPR Right to Access:</strong> Users can request their complete data. Use "User Data Export" above.</li>
          <li><strong>Legal Requests:</strong> Government or legal entity requests should include User ID and date range.</li>
          <li><strong>Data Retention:</strong> All exports include timestamps. Deleted users maintained for 30 days before permanent deletion.</li>
          <li><strong>Audit Trail:</strong> All export actions are logged in Activity Log with admin details.</li>
        </ul>
      </div>
    </div>
  );
};

export default ReportsPage;